import { useEffect, useState } from 'react';
import Button from '../common/Button.js';
import Alert from '../common/Alert.js';
import { mlSheetService } from '../../services/mlSheetService.js';

const toTimeInput = (value) => {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${String(match[1]).padStart(2, '0')}:${match[2]}`;
};

const parseClockMinutes = (hhmm) => {
  const match = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const formatClock = (hhmm) => {
  const match = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
};

const formatJourneyDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value || '';
  return `${match[3]}-${match[2]}-${match[1]}`;
};

const addDaysToIsoDate = (value, days) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setDate(date.getDate() + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Same-day: journey date. Overnight: next calendar day (full arrival day). */
const sheetArrivesNextDay = (sheet) => {
  if (!sheet) return false;
  if (sheet.arrives_next_day === true) return true;
  const dep = parseClockMinutes(sheet.departure_time || sheet.route?.departure_time);
  if (dep == null) return false;
  // Prefer route design when present so an extended sheet time past departure
  // still counts as overnight.
  const routeArr = parseClockMinutes(sheet.route?.arrival_time);
  const routeDep = parseClockMinutes(sheet.route?.departure_time) ?? dep;
  if (routeArr != null && routeArr <= routeDep) return true;
  const arr = parseClockMinutes(sheet.arrival_time);
  if (arr == null) return false;
  return arr <= dep;
};

const deadlineFrom = (journeyDate, hhmm, nextDay) => {
  const dateMatch = String(journeyDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const timeMatch = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!dateMatch || !timeMatch) return null;
  const day = new Date(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    0,
    0,
  );
  if (nextDay) {
    day.setDate(day.getDate() + 1);
  }
  return day;
};

const ExtendArrivalModal = ({ sheet, onClose, onSaved }) => {
  const [arrivalTime, setArrivalTime] = useState(toTimeInput(sheet?.arrival_time));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setArrivalTime(toTimeInput(sheet?.arrival_time));
  }, [sheet]);

  if (!sheet) return null;

  const overnight = sheetArrivesNextDay(sheet);
  const deadline = deadlineFrom(sheet.journey_date, arrivalTime, overnight);
  const timeStillAhead = Boolean(deadline && deadline.getTime() > Date.now());
  const arrivalDateIso = overnight
    ? addDaysToIsoDate(sheet.journey_date, 1)
    : sheet.journey_date;
  const arrivalDateLabel = formatJourneyDate(arrivalDateIso) || formatJourneyDate(sheet.journey_date);
  const journeyLabel = formatJourneyDate(sheet.journey_date);
  const chosenLabel = formatClock(arrivalTime);

  const save = async () => {
    if (!arrivalTime) {
      Alert.error('Enter the new arrival time');
      return;
    }
    if (!timeStillAhead) {
      Alert.error(
        chosenLabel
          ? `${chosenLabel} on ${arrivalDateLabel} has already passed. Choose a later time to open this sheet.`
          : 'Choose a later time to open this sheet.',
      );
      return;
    }
    try {
      setSaving(true);
      const response = await mlSheetService.extendArrival(sheet.ml_sheet_id, arrivalTime);
      Alert.success(response.data?.message || 'Arrival time updated');
      onSaved?.(response.data?.data);
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not update the arrival time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-4 sm:p-6" role="dialog" aria-modal="true">
        <h2 className="text-lg font-bold text-gray-800">Extend arrival time</h2>
        <p className="text-sm text-gray-600 mt-1 break-all">
          {sheet.ml_sheet_id}
          {journeyLabel ? ` · ${journeyLabel}` : ''}
        </p>
        <p className="text-sm text-gray-600 mt-2">
          {overnight
            ? 'This service arrives the next day. The sheet stays open until this arrival time on the arrival date (full next day).'
            : 'The sheet opens until this arrival time on the journey date. After that time, the next check closes it.'}
        </p>
        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Arrival time</label>
        <input
          type="time"
          value={arrivalTime}
          onChange={(event) => setArrivalTime(event.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
        />
        {chosenLabel ? (
          <p className={`text-sm mt-2 ${timeStillAhead ? 'text-green-700' : 'text-red-600'}`}>
            {timeStillAhead
              ? `This sheet will stay open until ${chosenLabel} on ${arrivalDateLabel}.`
              : `${chosenLabel} on ${arrivalDateLabel} has already passed. Choose a later time on ${arrivalDateLabel}.`}
          </p>
        ) : null}
        <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={saving || !timeStillAhead}>Update time</Button>
        </div>
      </div>
    </div>
  );
};

export default ExtendArrivalModal;
